import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye,
  BookOpen,
  Calendar,
  Clock,
  ChefHat,
  Utensils,
  Star,
  Download,
  Upload,
  Copy,
  MoreVertical,
  CheckCircle,
  XCircle
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

const MenuPage = () => {
  const { company } = useAuth();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showNewMenuModal, setShowNewMenuModal] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [newMenu, setNewMenu] = useState({
    name: '',
    description: '',
    date: '',
    meal_type: 'lunch',
    status: 'draft',
    items: []
  });

  const mealTypes = [
    { value: 'breakfast', label: 'Café da Manhã', icon: '🌅' },
    { value: 'lunch', label: 'Almoço', icon: '🍽️' },
    { value: 'snack', label: 'Lanche', icon: '🥪' },
    { value: 'dinner', label: 'Jantar', icon: '🌙' }
  ];

  const statusOptions = [
    { value: 'draft', label: 'Rascunho', color: 'bg-gray-100 text-gray-800' },
    { value: 'published', label: 'Publicado', color: 'bg-green-100 text-green-800' },
    { value: 'archived', label: 'Arquivado', color: 'bg-yellow-100 text-yellow-800' }
  ];

  useEffect(() => {
    if (company?.id) {
      loadMenus();
    }
  }, [company]);

  const loadMenus = async () => {
    try {
      setLoading(true);
      // TODO: Implementar dataHelpers.getMenus quando a função for criada
      const data = [
        {
          id: 1,
          name: 'Cardápio Segunda-feira',
          description: 'Menu especial da semana',
          date: '2024-01-15',
          meal_type: 'lunch',
          status: 'published',
          items: [
            { id: 1, name: 'Arroz', category: 'Acompanhamento' },
            { id: 2, name: 'Feijão', category: 'Acompanhamento' },
            { id: 3, name: 'Frango Grelhado', category: 'Proteína' }
          ],
          created_at: new Date().toISOString()
        }
      ];
      setMenus(data || []);
    } catch (error) {
      console.error('Error loading menus:', error);
      toast.error('Erro ao carregar cardápios');
      setMenus([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMenu = async () => {
    try {
      // TODO: Implementar dataHelpers.createMenu
      const menuData = {
        ...newMenu,
        company_id: company.id,
        created_at: new Date().toISOString()
      };
      
      console.log('Creating menu:', menuData);
      toast.success('Cardápio criado com sucesso!');
      
      setShowNewMenuModal(false);
      setNewMenu({
        name: '',
        description: '',
        date: '',
        meal_type: 'lunch',
        status: 'draft',
        items: []
      });
      
      loadMenus();
    } catch (error) {
      console.error('Error creating menu:', error);
      toast.error('Erro ao criar cardápio');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(s => s.value === status);
    return statusOption?.color || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const statusOption = statusOptions.find(s => s.value === status);
    return statusOption?.label || status;
  };

  const getMealTypeInfo = (mealType) => {
    const mealInfo = mealTypes.find(m => m.value === mealType);
    return mealInfo || { label: mealType, icon: '🍽️' };
  };

  const filteredMenus = menus.filter(menu => {
    const matchesSearch = menu.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         menu.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || menu.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
      {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <BookOpen className="w-8 h-8 mr-3 text-blue-600" />
              Cardápios
            </h1>
            <p className="text-gray-600">
              Gerencie os cardápios da sua cantina
            </p>
          </div>
          <div className="mt-4 sm:mt-0 space-x-2">
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
            <Button onClick={() => setShowNewMenuModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Novo Cardápio
            </Button>
          </div>
        </div>

      {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total de Cardápios</p>
                <p className="text-2xl font-semibold text-gray-900">{menus.length}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Publicados</p>
                <p className="text-2xl font-semibold text-green-600">
                  {menus.filter(m => m.status === 'published').length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Rascunhos</p>
                <p className="text-2xl font-semibold text-yellow-600">
                  {menus.filter(m => m.status === 'draft').length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <ChefHat className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Esta Semana</p>
                <p className="text-2xl font-semibold text-purple-600">
                  {menus.filter(m => {
                    const menuDate = new Date(m.date);
                    const now = new Date();
                    const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
                    const weekEnd = new Date(weekStart);
                    weekEnd.setDate(weekEnd.getDate() + 6);
                    return menuDate >= weekStart && menuDate <= weekEnd;
                  }).length}
                </p>
              </div>
            </div>
          </Card>
        </div>

      {/* Filters */}
        <Card className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar cardápios..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos os status</option>
              {statusOptions.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>

            <Button variant="outline" className="justify-center">
              <Filter className="w-4 h-4 mr-2" />
              Mais Filtros
            </Button>
          </div>
        </Card>

      {/* Menus Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMenus.length === 0 ? (
            <div className="col-span-full">
              <Card className="p-12 text-center">
                <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Nenhum cardápio encontrado
                </h3>
                <p className="text-gray-600 mb-6">
                  Comece criando seu primeiro cardápio
                </p>
                <Button onClick={() => setShowNewMenuModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Cardápio
                </Button>
              </Card>
            </div>
          ) : (
            filteredMenus.map((menu) => {
              const mealInfo = getMealTypeInfo(menu.meal_type);
              
              return (
                <Card key={menu.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                          <span className="text-lg">{mealInfo.icon}</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{menu.name}</h3>
                          <p className="text-sm text-gray-600">{mealInfo.label}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(menu.status)}`}>
                          {getStatusLabel(menu.status)}
                        </span>
                        <div className="relative">
                          <button className="p-1 rounded-full hover:bg-gray-100">
                            <MoreVertical className="w-4 h-4 text-gray-400" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm mb-4">{menu.description}</p>

                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(menu.date)}
                      </div>
                      <div className="flex items-center">
                        <Utensils className="w-4 h-4 mr-1" />
                        {menu.items?.length || 0} itens
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedMenu(menu);
                          setShowDetailsModal(true);
                        }}
                        className="flex-1"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      </div>

      {/* New Menu Modal */}
      {showNewMenuModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Novo Cardápio</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Cardápio
                </label>
                <input
                  type="text"
                  value={newMenu.name}
                  onChange={(e) => setNewMenu(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ex: Cardápio Segunda-feira"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={newMenu.description}
                  onChange={(e) => setNewMenu(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Descrição do cardápio..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data
                  </label>
                  <input
                    type="date"
                    value={newMenu.date}
                    onChange={(e) => setNewMenu(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Refeição
                  </label>
                  <select
                    value={newMenu.meal_type}
                    onChange={(e) => setNewMenu(prev => ({ ...prev, meal_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {mealTypes.map(meal => (
                      <option key={meal.value} value={meal.value}>
                        {meal.icon} {meal.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={newMenu.status}
                  onChange={(e) => setNewMenu(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {statusOptions.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="mt-6 flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowNewMenuModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateMenu}
                className="flex-1"
              >
                Criar Cardápio
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedMenu && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Detalhes do Cardápio</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedMenu.name}</h2>
                  <p className="text-gray-600">{selectedMenu.description}</p>
                </div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedMenu.status)}`}>
                  {getStatusLabel(selectedMenu.status)}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Data</label>
                  <p className="text-lg font-medium text-gray-900">{formatDate(selectedMenu.date)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Tipo de Refeição</label>
                  <p className="text-lg font-medium text-gray-900">
                    {getMealTypeInfo(selectedMenu.meal_type).icon} {getMealTypeInfo(selectedMenu.meal_type).label}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Itens do Cardápio</h4>
                {selectedMenu.items && selectedMenu.items.length > 0 ? (
                  <div className="space-y-2">
                    {selectedMenu.items.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <span className="font-medium text-gray-900">{item.name}</span>
                          <span className="text-gray-500 ml-2">• {item.category}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 italic">Nenhum item adicionado ainda</p>
                )}
              </div>
            </div>
            
            <div className="mt-6 flex space-x-3">
              <Button variant="outline" className="flex-1">
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
              <Button className="flex-1">
                <Copy className="w-4 h-4 mr-2" />
                Duplicar
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MenuPage;
